// 8. 演目順 並び替え（管理者向け・S4 実API）。D&Dで並び替え、同一会員が近い演目に出ると行の色で警告。
// 1演目前=赤／2演目前=黄／3演目前=緑（4以上は警告なし）の3段3色（仕様 §10.5⑧）。
// 距離はインデックス差で計算し、MC等の特別演目も距離に含める（MCを挟むと1つ開く挙動に一致）。
// 警告対象は会員のみ（ゲスト・講師は is_member=false で除外）。
// 並び替え＝Reset/Submit（PATCH order）。特別演目の出演者編集＝モーダル「完了」で即保存（PATCH cast）。
import { useCallback, useEffect, useMemo, useState } from "react";
import Breadcrumbs from "../../../components/Breadcrumbs";
import StatusMessage from "../../../components/common/StatusMessage";
import {
  getRecitalOrder,
  updateRecitalOrder,
  updateRecitalCast,
} from "../../../utils/recitalApi";
import "./recital.scss";

import { normalizeParts, serialOrder } from "./orderUtils";
const RC_BREADCRUMBS = [
  { title: "Home", path: "/" },
  { title: "Recital", path: "/recital/2026/order" },
  { title: "Program Order", path: "/recital/2026/order" },
];

// 各演目の近接警告クラスと、強調する会員（customer_id）を算出。会員のみ比較対象。
function computeWarnings(list) {
  const members = list.map((p) => p.cast.filter((c) => c.is_member).map((c) => c.id));
  return list.map((_, i) => {
    let best = 99;
    let hot = new Set();
    list.forEach((_q, j) => {
      if (i === j) return;
      const shared = members[i].filter((id) => members[j].includes(id));
      if (shared.length) {
        const d = Math.abs(i - j);
        if (d < best) {
          best = d;
          hot = new Set(shared);
        } else if (d === best) {
          shared.forEach((id) => hot.add(id));
        }
      }
    });
    const cls = best === 1 ? "red" : best === 2 ? "yellow" : best === 3 ? "green" : "";
    return { cls, hot };
  });
}


export default function RecitalOrderPage() {
  const [list, setList] = useState([]);
  const [numParts, setNumParts] = useState(1);
  const [baseline, setBaseline] = useState([]); // 読み込み時の並び（Reset 先）
  // DB に保存済みの並び。正規化で表示が変わった（部未設定の新規演目など）場合も未保存扱いにして Submit できるようにする
  const [savedSerial, setSavedSerial] = useState("");
  const [attendees, setAttendees] = useState([]); // 特別演目ピッカー候補（参加会員）
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [msgType, setMsgType] = useState("success");
  const [dragId, setDragId] = useState(null);
  const [castEditId, setCastEditId] = useState(null); // 出演者編集中の演目 id
  const [castDraft, setCastDraft] = useState(null); // 編集中の会員ID Set（完了で保存）
  const [castQ, setCastQ] = useState("");
  const [busy, setBusy] = useState(false);

  const notify = useCallback((text, type = "success") => {
    setMsgType(type);
    setMessage(text);
    setTimeout(() => setMessage(""), 3000);
  }, []);

  // 初期ロード
  useEffect(() => {
    let alive = true;
    getRecitalOrder()
      .then((d) => {
        if (!alive) return;
        const np = d.num_parts || 1;
        const progs = normalizeParts(d.programs || [], np);
        setList(progs);
        setBaseline(progs);
        setSavedSerial(serialOrder(d.programs || []));
        setNumParts(np);
        setAttendees(d.attendees || []);
        setLoading(false);
      })
      .catch((e) => {
        if (!alive) return;
        setLoading(false);
        notify(e.message || "読み込みに失敗しました", "error");
      });
    return () => {
      alive = false;
    };
  }, [notify]);

  const warnings = useMemo(() => computeWarnings(list), [list]);

  // 並び or 部が保存済みと変わっているか（Reset/Submit の有効化判定）。cast は即保存のため対象外。
  const dirty = useMemo(
    () => serialOrder(list) !== savedSerial,
    [list, savedSerial],
  );

  const openCastEdit = (p) => {
    setCastQ("");
    setCastEditId(p.id);
    setCastDraft(new Set(p.cast.filter((c) => c.is_member).map((c) => c.id)));
  };
  const closeCastEdit = () => {
    setCastEditId(null);
    setCastDraft(null);
  };
  const castEditProg = list.find((p) => p.id === castEditId) || null;
  const toggleCastDraft = (cid) =>
    setCastDraft((s) => {
      const next = new Set(s);
      if (next.has(cid)) next.delete(cid);
      else next.add(cid);
      return next;
    });

  // 出演者編集の保存（PATCH cast）→ 返却演目で list/baseline を置換（cast は dirty に含めない）
  const saveCast = async () => {
    if (busy || !castEditProg) return;
    setBusy(true);
    try {
      const d = await updateRecitalCast(castEditProg.id, [...castDraft]);
      const repl = (arr) =>
        arr.map((p) => (p.id === d.program.id ? { ...p, cast: d.program.cast } : p));
      setList(repl);
      setBaseline(repl);
      closeCastEdit();
      notify("出演者を保存しました");
    } catch (e) {
      notify(e.message || "保存に失敗しました", "error");
    } finally {
      setBusy(false);
    }
  };

  // 演目の上にドロップ＝その演目の直前へ＋同じ部へ
  const onDrop = (targetId) => {
    if (!dragId || dragId === targetId) return;
    setMessage("");
    setList((cur) => {
      const from = cur.findIndex((x) => x.id === dragId);
      const target = cur.find((x) => x.id === targetId);
      if (from < 0 || !target) return cur;
      const moved = { ...cur[from], part: target.part };
      const next = cur.filter((x) => x.id !== dragId);
      const to = next.findIndex((x) => x.id === targetId);
      next.splice(to, 0, moved);
      return next.sort((a, b) => a.part - b.part); // 部ごとに連続維持（安定ソート）
    });
    setDragId(null);
  };

  // 部セクションの空き領域にドロップ＝その部の末尾へ移動
  const onDropToPart = (part) => {
    if (!dragId) return;
    setMessage("");
    setList((cur) => {
      const idx = cur.findIndex((x) => x.id === dragId);
      if (idx < 0 || (cur[idx].part || 1) === part) return cur;
      const moved = { ...cur[idx], part };
      const next = cur.filter((x) => x.id !== dragId);
      next.push(moved); // 末尾→安定ソートで該当部の末尾に収まる
      return next.sort((a, b) => a.part - b.part);
    });
    setDragId(null);
  };

  const handleReset = () => {
    setList(baseline);
    setMessage("");
  };
  const handleSubmit = async () => {
    if (busy || !dirty) return;
    setBusy(true);
    try {
      await updateRecitalOrder(list.map((p) => ({ id: p.id, part: p.part || 1 })));
      setBaseline(list);
      setSavedSerial(serialOrder(list));
      notify("保存しました");
    } catch (e) {
      notify(e.message || "保存に失敗しました", "error");
    } finally {
      setBusy(false);
    }
  };

  const attHits = attendees.filter(
    (a) => castQ === "" || a.name.includes(castQ) || a.kana.includes(castQ),
  );

  // 演目行（displayNum=部内の連番／gi=全体位置・警告参照用）
  const renderRow = (p, displayNum, gi) => {
    const isDance = (p.entry_type || "dance") === "dance";
    return (
    <div
      key={p.id}
      className={`rc-prog ${p.special ? "special" : ""} ${warnings[gi].cls} ${
        dragId === p.id ? "drag" : ""
      }`}
      draggable
      data-verify-pos={gi + 1}
      data-verify-warn={warnings[gi].cls || "none"}
      onDragStart={() => setDragId(p.id)}
      onDragEnd={() => setDragId(null)}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.stopPropagation();
        onDrop(p.id);
      }}
    >
      <span className="grip">⠿</span>
      <div className="num">{`${p.part || 1}-${displayNum}`}</div>
      <div className="body">
        <div className="ttl">
          {p.label || p.title}
          <span className="meta">{p.member_count}名</span>
        </div>
        <div className="cast">
          {p.special && !isDance ? null : p.special ? (
            <>
              {p.cast.length > 0 ? (
                p.cast.map((c) => (
                  <span
                    key={c.id}
                    className={`chip${
                      c.is_member && warnings[gi].hot.has(c.id) ? " hot" : ""
                    }`}
                  >
                    {c.name}
                  </span>
                ))
              ) : (
                <span style={{ color: "#9aa1ad" }}>
                  申込には非掲載 ／ 出演者は手動で追加
                </span>
              )}
              <button
                type="button"
                className="rc-cast-add"
                draggable={false}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={() => openCastEdit(p)}
              >
                ＋ 出演者を{p.cast.length ? "編集" : "追加"}
              </button>
            </>
          ) : (
            p.cast.map((c) => (
              <span
                key={c.id}
                className={`chip${
                  c.is_member && warnings[gi].hot.has(c.id) ? " hot" : ""
                }`}
              >
                {c.name}
              </span>
            ))
          )}
        </div>
      </div>
    </div>
    );
  };

  return (
    <div className="editarea rc-order">
      <Breadcrumbs breadcrumbs={RC_BREADCRUMBS} />
      <StatusMessage message={message} type={msgType} />
      <div className="rc-order-inner">
        <div className="rc-bar">
          <h3>演目順 並び替え</h3>
        </div>
        <p className="rc-lead">
          ドラッグ＆ドロップで並び替え
          {numParts > 1 && "・各部へ振り分け"}。部数は基本設定で変更します。
        </p>

        {loading && <p className="rc-lead">読み込んでいます…</p>}
        {!loading && list.length === 0 && (
          <p className="rc-lead">演目がありません。先に演目構成で追加してください。</p>
        )}

        {!loading &&
          list.length > 0 &&
          Array.from({ length: numParts }, (_, pi) => {
            const partNum = pi + 1;
            const items = list.filter((p) => (p.part || 1) === partNum);
            return (
              <div
                key={partNum}
                className="rc-part-group"
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => onDropToPart(partNum)}
              >
                {numParts > 1 && (
                  <div className="rc-part-head">
                    <span>{partNum}部</span>
                  </div>
                )}
                {items.length === 0 ? (
                  <div className="rc-part-empty">
                    ここにドラッグして{partNum}部へ
                  </div>
                ) : (
                  items.map((p, li) => renderRow(p, li + 1, list.indexOf(p)))
                )}
              </div>
            );
          })}
      </div>

      <div className="submit-area">
        <button type="button" onClick={handleReset} disabled={!dirty || busy}>
          Reset
        </button>
        <button type="button" onClick={handleSubmit} disabled={!dirty || busy}>
          Submit
        </button>
      </div>

      {castEditProg && (
        <div className="rc-modal-ov" onClick={closeCastEdit}>
          <div
            className="rc-modal rc-cast-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mh">
              出演者を選択：{castEditProg.title}
              <span className="x" onClick={closeCastEdit}>
                ✕
              </span>
            </div>
            <div className="mb">
              <p style={{ margin: "0 0 10px", fontSize: 12, color: "#9aa1ad" }}>
                参加者一覧（参加）から出演者を選びます。選択中：
                {castDraft ? castDraft.size : 0}名
              </p>
              <div className="rc-cast-search">
                <i className="fa-thin fa-magnifying-glass"></i>
                <input
                  type="text"
                  placeholder="氏名・フリガナで検索"
                  value={castQ}
                  onChange={(e) => setCastQ(e.target.value)}
                />
              </div>
              {attHits.length === 0 ? (
                <p
                  style={{
                    margin: "14px 0",
                    textAlign: "center",
                    color: "#8a8794",
                    fontSize: 12.5,
                  }}
                >
                  該当する参加者はいません
                </p>
              ) : (
                <div className="rc-cast-pick">
                  {attHits.map((a) => {
                    const on = castDraft ? castDraft.has(a.id) : false;
                    return (
                      <label key={a.id} className={on ? "on" : ""}>
                        <input
                          type="checkbox"
                          checked={on}
                          onChange={() => toggleCastDraft(a.id)}
                        />
                        {a.name}
                      </label>
                    );
                  })}
                </div>
              )}
              <button
                className="rc-btn pri"
                style={{ width: "100%", marginTop: 14 }}
                onClick={saveCast}
                disabled={busy}
              >
                {busy ? "保存中…" : "完了"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
